/**
 * The gate and the routes.
 *
 * AppStateProvider around everything, then one screen by `status`:
 *
 *   loading       index.html's skeleton — a spinner only if /me is slow
 *   signedOut     straight to mauvely.com to sign in
 *   secondFactor  straight to the second-factor step there
 *   unreachable   "Assist did not answer." and Retry
 *   ready         AI off → the off screen; an app's panel, onboarded or not;
 *                 not onboarded → the interview; otherwise the shell, with
 *                 these in its main region:
 *
 *   /                          Home
 *   /chat/:id                  Chat
 *   /project/:id               Project
 *   /gallery                   Gallery
 *   /dispatch                  Dispatch: the PC used last, or none yet
 *   /dispatch/:id              a PC's thread
 *   /assistant                 the Android assistant's sheet, outside the shell
 *   /panel?app=<key>           a Mauvely app's panel, outside the shell
 *   /a/:token                  a shared artifact, outside the gate: no account needed
 *   /settings                  Settings, Usage tab
 *   /settings/:tab             Settings — usage | models | connectors | computer | memory | skills | design | automations
 *   /settings/memory/:docId    Settings, a memory doc
 *
 * The sign-in round trip goes where the Worker's 401 says (`signInUrl`). The
 * fallback, for a Worker that does not say, is the same address built here —
 * main's origin, with the localhost arm because main runs on :8787 locally.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { announce } from './android';
import { ds } from './ds';
import { BootSkeleton } from './components/BootSkeleton';
import { MainHeader, Shell } from './components/Shell';
import { Ph } from './components/ui';
import { discardTemporary, leftChat } from './hooks/temporaryChats';
import { AiOff } from './pages/AiOff';
import { ChatPage } from './pages/Chat';
import { DispatchIndex, DispatchPage } from './pages/Dispatch';
import { HomePage } from './pages/Home';
import { GalleryPage } from './pages/Gallery';
import { ArtifactsPage } from './pages/Artifacts';
import { AssistantPage } from './pages/Assistant';
import { OnboardingPage } from './pages/Onboarding';
import { PanelPage } from './pages/Panel';
import { ProjectPage } from './pages/Project';
import { SettingsPage, type SettingsTab } from './pages/Settings';
import { SharedPage } from './pages/Shared';
import { matchRoute, useRouter } from './router';
import { AppStateProvider, useApp } from './state';

const TABS: readonly SettingsTab[] = ['usage', 'models', 'connectors', 'computer', 'memory', 'skills', 'design', 'automations', 'developer'];

function accountOrigin(): string {
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `${protocol}//${hostname}:8787`;
  return 'https://mauvely.com';
}

function Ground({ children }: { children?: ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        height: '100%',
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
        background: 'var(--app-ground)',
        color: 'var(--text-body)',
        fontFamily: 'var(--font-body)'
      }}
    >
      {children}
    </div>
  );
}

/** The quiet wait: the skeleton index.html first painted, and a spinner after
 *  400ms so a slow load does not look broken. */
export function Waiting() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 400);
    return () => clearTimeout(t);
  }, []);
  return <BootSkeleton>{slow ? <Ph name="circle-notch" size={22} className="spin" color="var(--text-faint)" /> : null}</BootSkeleton>;
}

/** Leaves for another page and shows the wait meanwhile. */
function Away({ to }: { to: string }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return <Waiting />;
}

function Unreachable({ onRetry }: { onRetry: () => Promise<void> }) {
  const { Wordmark, Button } = ds();
  const [trying, setTrying] = useState(false);
  return (
    <Ground>
      <main
        style={{
          width: '100%',
          maxWidth: 420,
          boxSizing: 'border-box',
          background: 'var(--app-panel)',
          borderRadius: 'var(--radius-region)',
          padding: '28px 28px 30px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 16
        }}
      >
        <Wordmark lockup="ink" word="assist" badge="sparkle" size={17} />
        <h1
          style={{
            margin: '6px 0 0',
            fontFamily: 'var(--font-display)',
            fontWeight: 800,
            fontSize: 28,
            lineHeight: 1.05,
            letterSpacing: 'normal',
            color: 'var(--text-strong)'
          }}
        >
          Assist did not answer.
        </h1>
        <Button
          variant="accent"
          density="app"
          size="sm"
          iconLeft="arrows-clockwise"
          disabled={trying}
          onClick={() => {
            setTrying(true);
            void onRetry().finally(() => setTrying(false));
          }}
        >
          Retry
        </Button>
      </main>
    </Ground>
  );
}

function Routes() {
  const { path, navigate } = useRouter();

  const chat = matchRoute('/chat/:id', path);
  const project = matchRoute('/project/:id', path);
  const pc = matchRoute('/dispatch/:deviceId', path);
  const memoryDoc = matchRoute('/settings/memory/:docId', path);
  const tab = matchRoute('/settings/:tab', path)?.tab;
  const settingsTab = path === '/settings' || path === '/settings/' ? 'usage' : TABS.find((t) => t === tab) ?? null;
  const known = path === '/' || path === '/gallery' || path === '/artifacts' || path === '/dispatch' || !!chat || !!pc || !!project || !!memoryDoc || !!settingsTab;

  // Anything else goes home rather than drawing an empty window.
  useEffect(() => {
    if (!known) navigate('/', { replace: true });
  }, [known, navigate]);

  // Leaving a temporary chat deletes it. The path before is kept in a ref, not
  // read in a cleanup: StrictMode runs this twice on mount, and the second run
  // sees the same path and does nothing.
  const before = useRef(path);
  useEffect(() => {
    const left = leftChat(before.current, path);
    before.current = path;
    if (left) void discardTemporary(left);
  }, [path]);

  if (chat) return <ChatPage key={chat.id} id={chat.id!} />;
  // Home draws its own header, with the temporary chat toggle in it.
  if (path === '/') return <HomePage />;
  if (path === '/gallery') return <GalleryPage />;
  if (path === '/artifacts') return <ArtifactsPage />;
  if (path === '/dispatch') return <DispatchIndex />;
  if (pc) return <DispatchPage key={pc.deviceId} deviceId={pc.deviceId!} />;

  return (
    <>
      <MainHeader />
      {project ? (
        <ProjectPage key={project.id} id={project.id!} />
      ) : memoryDoc ? (
        <SettingsPage tab="memory" docId={memoryDoc.docId!} />
      ) : settingsTab ? (
        <SettingsPage tab={settingsTab} docId={null} />
      ) : null}
    </>
  );
}

/**
 * The Worker's sign-in address, sent back to this page rather than the site
 * root it names. The Worker cannot know which chat was open; a link to one
 * should land on it after signing in. website-main still checks `next`
 * against RETURN_HOSTS, so this widens nothing.
 */
function returnHere(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.set('next', window.location.href);
    return u.toString();
  } catch {
    return url;
  }
}

function Gate() {
  const { status, errorDetails, me, refreshMe } = useApp();
  const { path } = useRouter();
  // The phone's assistant sheet draws over another app: nothing opaque, not
  // even the wait.
  const sheet = path === '/assistant';

  // The first screen is drawn: the Android app lifts its own loading screen.
  const drawn = status !== 'loading';
  useEffect(() => {
    if (drawn) announce();
  }, [drawn]);

  if (status === 'loading') return sheet ? null : <Waiting />;

  if (status === 'signedOut') {
    const url = errorDetails.signInUrl;
    return (
      <Away
        to={
          typeof url === 'string'
            ? returnHere(url)
            : `${accountOrigin()}/signin?next=${encodeURIComponent(window.location.href)}`
        }
      />
    );
  }

  if (status === 'secondFactor') {
    // Not /signin: that would mint another session in the same state and loop.
    const url = errorDetails.twoFactorUrl;
    return (
      <Away
        to={
          typeof url === 'string'
            ? returnHere(url)
            : `${accountOrigin()}/signin/two-factor?next=${encodeURIComponent(window.location.href)}`
        }
      />
    );
  }

  if (status === 'unreachable' || !me) return <Unreachable onRetry={refreshMe} />;
  if (!me.ai.allowed) return <AiOff me={me} />;
  // Inside another app, the interview does not stand in the way.
  if (path === '/panel') return <PanelPage />;
  if (!me.onboarded) return <OnboardingPage />;
  if (sheet) return <AssistantPage />;

  return (
    <Shell>
      <Routes />
    </Shell>
  );
}

export function App() {
  const { path } = useRouter();
  // A shared artifact's link opens without an account, outside the gate.
  const shared = matchRoute('/a/:token', path);
  if (shared) return <SharedPage token={shared.token!} />;
  return (
    <AppStateProvider>
      <Gate />
    </AppStateProvider>
  );
}
